import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import type { ConsentTypeValue } from '@/models/consent';
import type { EventTypeValue } from '@/models/event';
import { normalizePermissions } from '@/models/permissions';
import type { Birthday, Role, User, Veganniversary } from '@/models/user';
import { CalendarFeedScope, type CalendarFeedScopeValue } from '@/models/user';

import { apiClient, authClient, getCurrentAccessToken } from './client';

// --- Wire types (snake_case, server-shaped). ----------------------------------

interface WireRole {
  id: string;
  name: string;
  is_default: boolean;
  permissions: string[];
}

interface WireBirthday {
  month: number;
  day: number;
  year: number | null;
}

interface WireVeganniversary {
  month: number;
  day: number | null;
  year: number;
}

interface WireUser {
  id: string;
  phone_number: string;
  first_name?: string;
  last_name?: string;
  full_name: string;
  nickname?: string;
  email?: string;
  bio?: string;
  pronouns?: string;
  birthday?: WireBirthday | null;
  veganniversary?: WireVeganniversary | null;
  is_member?: boolean;
  is_superuser?: boolean;
  is_staff?: boolean;
  needs_onboarding: boolean;
  needs_password_reset?: boolean;
  needs_guidelines_consent?: boolean;
  needs_sms_consent?: boolean;
  needs_contact_privacy_consent?: boolean;
  show_phone?: boolean;
  show_email?: boolean;
  show_birthday?: boolean;
  show_veganniversary?: boolean;
  veganniversary_shoutout_opt_in?: boolean;
  has_seen_veganniversary?: boolean;
  hide_last_name?: boolean;
  weekly_digest_opt_out?: boolean;
  monthly_digest_opt_out?: boolean;
  whatsapp_reminder_opt_out?: boolean;
  attendance_nudge_opt_out?: boolean;
  host_digest_opt_out?: boolean;
  week_start?: 'sunday' | 'monday';
  calendar_feed_scope?: CalendarFeedScopeValue;
  calendar_feed_excluded_types?: EventTypeValue[];
  profile_photo_url?: string;
  photo_updated_at?: string | null;
  roles: WireRole[];
}

interface TokenOut {
  // No `refresh`: the refresh token arrives via the httpOnly cookie, never the body.
  access: string;
}

interface AccessOut {
  access: string;
}

// --- Mapping helpers. ---------------------------------------------------------

function mapRole(r: WireRole): Role {
  return {
    id: r.id,
    name: r.name,
    isDefault: r.is_default,
    permissions: normalizePermissions(r.permissions),
  };
}

function mapBirthday(b: WireBirthday | null | undefined): Birthday | null {
  if (!b) return null;
  return { month: b.month, day: b.day, year: b.year };
}

function mapVeganniversary(v: WireVeganniversary | null | undefined): Veganniversary | null {
  if (!v) return null;
  return { month: v.month, day: v.day, year: v.year };
}

function mapUser(u: WireUser): User {
  return {
    id: u.id,
    phoneNumber: u.phone_number,
    firstName: u.first_name ?? '',
    lastName: u.last_name ?? '',
    fullName: u.full_name,
    nickname: u.nickname ?? '',
    email: u.email ?? '',
    bio: u.bio ?? '',
    pronouns: u.pronouns ?? '',
    birthday: mapBirthday(u.birthday),
    veganniversary: mapVeganniversary(u.veganniversary),
    isMember: u.is_member ?? true,
    isSuperuser: u.is_superuser ?? false,
    isStaff: u.is_staff ?? false,
    needsOnboarding: u.needs_onboarding,
    needsPasswordReset: u.needs_password_reset ?? false,
    needsGuidelinesConsent: u.needs_guidelines_consent ?? false,
    needsSmsConsent: u.needs_sms_consent ?? false,
    needsContactPrivacyConsent: u.needs_contact_privacy_consent ?? false,
    showPhone: u.show_phone ?? false,
    showEmail: u.show_email ?? false,
    showBirthday: u.show_birthday ?? false,
    showVeganniversary: u.show_veganniversary ?? true,
    veganniversaryShoutoutOptIn: u.veganniversary_shoutout_opt_in ?? true,
    hasSeenVeganniversary: u.has_seen_veganniversary ?? false,
    hideLastName: u.hide_last_name ?? false,
    weeklyDigestOptOut: u.weekly_digest_opt_out ?? false,
    monthlyDigestOptOut: u.monthly_digest_opt_out ?? false,
    whatsappReminderOptOut: u.whatsapp_reminder_opt_out ?? false,
    attendanceNudgeOptOut: u.attendance_nudge_opt_out ?? false,
    hostDigestOptOut: u.host_digest_opt_out ?? false,
    weekStart: u.week_start ?? 'sunday',
    calendarFeedScope: u.calendar_feed_scope ?? CalendarFeedScope.All,
    calendarFeedExcludedTypes: u.calendar_feed_excluded_types ?? [],
    profilePhotoUrl: u.profile_photo_url ?? '',
    photoUpdatedAt: u.photo_updated_at ?? null,
    roles: u.roles.map(mapRole),
  };
}

// --- Endpoints. ---------------------------------------------------------------

export async function login(
  phoneNumber: string,
  password: string,
): Promise<{ access: string; user: User }> {
  const { data } = await authClient.post<TokenOut>('/api/auth/login/', {
    phone_number: phoneNumber,
    password,
  });
  const user = await fetchMeWithToken(data.access);
  return { access: data.access, user };
}

export async function magicLogin(token: string): Promise<{ access: string; user: User }> {
  // Forward the current access token (if any) so the backend's cross-user
  // guard can detect that a different user is already signed in and reject
  // the link rather than silently swapping sessions.
  const current = getCurrentAccessToken();
  const config = current ? { headers: { Authorization: `Bearer ${current}` } } : undefined;
  const { data } = await authClient.get<TokenOut>(`/api/auth/magic-login/${token}/`, config);
  const user = await fetchMeWithToken(data.access);
  return { access: data.access, user };
}

// Hears the refreshed access token before /me resolves, or null once the visitor is known to be anonymous.
export type SessionTokenListener = (token: string | null) => void;

async function tryRestoreSession(
  onSessionToken?: SessionTokenListener,
): Promise<{ access: string; user: User } | null> {
  const { data } = await authClient.post<AccessOut>('/api/auth/refresh/', {});
  onSessionToken?.(data.access);
  const user = await fetchMeWithToken(data.access);
  return { access: data.access, user };
}

export async function restoreSession(
  onSessionToken?: SessionTokenListener,
): Promise<{ access: string; user: User } | null> {
  // Retry once unless it's a real 401 — avoids re-login on a transient 5xx.
  try {
    return await tryRestoreSession(onSessionToken);
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 401) {
      onSessionToken?.(null);
      return null;
    }
    try {
      return await tryRestoreSession(onSessionToken);
    } catch {
      return null;
    }
  }
}

export async function logout(): Promise<void> {
  try {
    await authClient.post('/api/auth/logout/');
  } catch {
    // Idempotent; server-side clear isn't critical if the client already forgot.
  }
}

export async function deleteAccount(password: string): Promise<void> {
  await apiClient.post('/api/auth/me/delete/', { password });
}

export async function fetchMe(): Promise<User> {
  const { data } = await apiClient.get<WireUser>('/api/auth/me/');
  return mapUser(data);
}

async function fetchMeWithToken(access: string): Promise<User> {
  // Used during login/magic-login/refresh before the store has the token yet.
  const { data } = await authClient.get<WireUser>('/api/auth/me/', {
    headers: { Authorization: `Bearer ${access}` },
  });
  return mapUser(data);
}

export async function completeOnboarding(payload: {
  newPassword: string;
  firstName?: string | undefined;
  lastName?: string | undefined;
  email?: string | undefined;
  pronouns?: string | undefined;
  consentTypes?: ConsentTypeValue[] | undefined;
}): Promise<User> {
  const { data } = await apiClient.post<WireUser>('/api/auth/complete-onboarding/', {
    new_password: payload.newPassword,
    first_name: payload.firstName,
    last_name: payload.lastName,
    email: payload.email,
    pronouns: payload.pronouns,
    consent_types: payload.consentTypes ?? [],
  });
  return mapUser(data);
}

export async function acceptConsents(consentTypes: ConsentTypeValue[]): Promise<User> {
  // records the given consents; accepting "guidelines" clears the hard login gate
  const { data } = await apiClient.post<WireUser>('/api/auth/accept-consents/', {
    consent_types: consentTypes,
  });
  return mapUser(data);
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await apiClient.post('/api/auth/change-password/', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

export interface ProfileUpdate {
  firstName?: string;
  lastName?: string;
  nickname?: string;
  email?: string;
  bio?: string;
  pronouns?: string;
  // null clears the stored birthday; a Birthday sets it (year is optional).
  birthday?: Birthday | null;
  veganniversary?: Veganniversary | null;
  showPhone?: boolean;
  showEmail?: boolean;
  showBirthday?: boolean;
  showVeganniversary?: boolean;
  veganniversaryShoutoutOptIn?: boolean;
  hasSeenVeganniversary?: boolean;
  hideLastName?: boolean;
  weeklyDigestOptOut?: boolean;
  monthlyDigestOptOut?: boolean;
  whatsappReminderOptOut?: boolean;
  attendanceNudgeOptOut?: boolean;
  hostDigestOptOut?: boolean;
  weekStart?: 'sunday' | 'monday';
  calendarFeedScope?: CalendarFeedScopeValue;
  calendarFeedExcludedTypes?: EventTypeValue[];
}

export async function updateProfile(patch: ProfileUpdate): Promise<User> {
  // Omit undefined so PATCH doesn't clobber fields that weren't explicitly set.
  const body: Record<string, unknown> = {};
  if (patch.firstName !== undefined) body.first_name = patch.firstName;
  if (patch.lastName !== undefined) body.last_name = patch.lastName;
  if (patch.nickname !== undefined) body.nickname = patch.nickname;
  if (patch.email !== undefined) body.email = patch.email;
  if (patch.bio !== undefined) body.bio = patch.bio;
  if (patch.pronouns !== undefined) body.pronouns = patch.pronouns;
  if (patch.birthday !== undefined) {
    body.birthday = patch.birthday
      ? { month: patch.birthday.month, day: patch.birthday.day, year: patch.birthday.year }
      : null;
  }
  if (patch.veganniversary !== undefined) {
    body.veganniversary = patch.veganniversary
      ? {
          month: patch.veganniversary.month,
          day: patch.veganniversary.day,
          year: patch.veganniversary.year,
        }
      : null;
  }
  if (patch.showPhone !== undefined) body.show_phone = patch.showPhone;
  if (patch.showEmail !== undefined) body.show_email = patch.showEmail;
  if (patch.showBirthday !== undefined) body.show_birthday = patch.showBirthday;
  if (patch.showVeganniversary !== undefined) body.show_veganniversary = patch.showVeganniversary;
  if (patch.veganniversaryShoutoutOptIn !== undefined)
    body.veganniversary_shoutout_opt_in = patch.veganniversaryShoutoutOptIn;
  if (patch.hasSeenVeganniversary !== undefined)
    body.has_seen_veganniversary = patch.hasSeenVeganniversary;
  if (patch.hideLastName !== undefined) body.hide_last_name = patch.hideLastName;
  if (patch.weeklyDigestOptOut !== undefined) body.weekly_digest_opt_out = patch.weeklyDigestOptOut;
  if (patch.monthlyDigestOptOut !== undefined)
    body.monthly_digest_opt_out = patch.monthlyDigestOptOut;
  if (patch.whatsappReminderOptOut !== undefined)
    body.whatsapp_reminder_opt_out = patch.whatsappReminderOptOut;
  if (patch.attendanceNudgeOptOut !== undefined)
    body.attendance_nudge_opt_out = patch.attendanceNudgeOptOut;
  if (patch.hostDigestOptOut !== undefined) body.host_digest_opt_out = patch.hostDigestOptOut;
  if (patch.weekStart !== undefined) body.week_start = patch.weekStart;
  if (patch.calendarFeedScope !== undefined) body.calendar_feed_scope = patch.calendarFeedScope;
  if (patch.calendarFeedExcludedTypes !== undefined)
    body.calendar_feed_excluded_types = patch.calendarFeedExcludedTypes;
  const { data } = await apiClient.patch<WireUser>('/api/auth/me/', body);
  return mapUser(data);
}

export async function uploadProfilePhoto(file: File): Promise<User> {
  const formData = new FormData();
  formData.append('photo', file, file.name);
  const { data } = await apiClient.post<WireUser>('/api/auth/me/photo/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return mapUser(data);
}

export async function deleteProfilePhoto(): Promise<User> {
  const { data } = await apiClient.delete<WireUser>('/api/auth/me/photo/');
  return mapUser(data);
}

// --- request login link -----------------------------------------------------

export type RequestLoginLinkDelivery = 'email' | 'admin' | 'cooldown';

interface RequestLoginLinkOut {
  detail: string;
  delivery: RequestLoginLinkDelivery;
  // Seconds until another link may be requested — only present on cooldown.
  retryAfterSeconds: number | null;
}

export function useRequestLoginLink() {
  // Endpoint always returns 200 (anti-enumeration). The `delivery` field
  // distinguishes "we emailed you" from "an admin will follow up" so the
  // UI can render honest copy — the unknown-phone case is bundled into the
  // admin path so the response shape doesn't reveal whether the account
  // exists when no email was sent.
  return useMutation({
    mutationFn: async (phoneNumber: string): Promise<RequestLoginLinkOut> => {
      const { data } = await authClient.post<{
        detail: string;
        delivery: RequestLoginLinkDelivery;
        retry_after_seconds: number | null;
      }>('/api/community/request-login-link/', { phone_number: phoneNumber });
      return {
        detail: data.detail,
        delivery: data.delivery,
        retryAfterSeconds: data.retry_after_seconds,
      };
    },
  });
}
